/**
 * Wireframe placeholder for the CRM rep token.
 * The live list has no id/token yet, so this string is never put in a subject.
 */
export const REP_TOKEN_PLACEHOLDER = "[rep token]"

/**
 * Headquarters card used when a territory has no rep, and when REPS_API_URL is unset.
 * Phone and email match site settings (formToEmail + phone) and the footer office in Viby J.
 */
export const INQUIRY_CC = "contact@solarlab.global"

export const HQ_FALLBACK = {
  name: "SolarLab a/s",
  title: "Headquarters",
  company: "Headquarters",
  city: "Viby J",
  region: "Denmark",
  country: "Denmark",
  email: "contact@solarlab.global",
  phone: "+45 93 100 108",
  languages: [],
  id: "",
  token: null,
  isHeadquarters: true,
  territories: [],
}

const TOKEN_FIELDS = ["token", "id"]
const LEGAL_SUFFIXES = new Set([
  "as",
  "aps",
  "inc",
  "ltd",
  "llc",
  "gmbh",
  "co",
  "corp",
  "plc",
  "pty",
  "lp",
  "sa",
  "ag",
  "bv",
  "nv",
])

function isFilled(value) {
  return value != null && String(value).trim() !== ""
}

/** Site settings store both office numbers in one PDF header field. A tel link needs one number. */
export function singlePhone(value) {
  if (!isFilled(value)) return ""
  const text = String(value).trim()
  const pluses = text.match(/\+/g) || []
  if (pluses.length > 1 || text.includes("/")) return ""
  return text
}

export function headquartersContactFromSettings(globalSettings) {
  const contacts = globalSettings?.footer?.contact || []
  let phone = ""

  for (const item of contacts) {
    const offices = item?.offices || []
    const isDenmarkHeadquarters = offices.some((office) =>
      /viby|denmark/i.test(`${office?.title || ""}\n${office?.address || ""}`)
    )
    if (!isDenmarkHeadquarters) continue
    phone = singlePhone(item?.phone)
    if (phone) break
  }

  if (!phone) phone = singlePhone(globalSettings?.phone)

  return {
    phone,
    email: isFilled(globalSettings?.formToEmail)
      ? String(globalSettings.formToEmail).trim()
      : "",
  }
}

export function readRepToken(raw) {
  if (!raw || typeof raw !== "object") return null

  for (const key of TOKEN_FIELDS) {
    const value = raw[key]
    if (value == null) continue
    const text = String(value).trim()
    if (!text || text === REP_TOKEN_PLACEHOLDER) continue
    return text
  }

  return null
}

export function cityFromHeadquartersAddress(address) {
  if (!isFilled(address)) return null

  const lines = String(address)
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  for (const line of lines) {
    const match = line.match(/\b\d{4}\s*,\s*(.+)$/)
    if (match?.[1]) return match[1].trim()
  }

  return null
}

export function buildHeadquarters({ phone, email, company } = {}) {
  const headquarters = { ...HQ_FALLBACK }

  const dialablePhone = singlePhone(phone)
  if (dialablePhone) headquarters.phone = dialablePhone
  if (isFilled(email)) headquarters.email = String(email).trim()

  if (company && typeof company === "object") {
    if (isFilled(company.name)) headquarters.name = String(company.name).trim()
    if (isFilled(company.phone)) headquarters.phone = String(company.phone).trim()
    if (isFilled(company.email)) headquarters.email = String(company.email).trim()
    const city = cityFromHeadquartersAddress(company.address)
    if (city) headquarters.city = city
  }

  return headquarters
}

export function headquartersOnlyDirectory(siteContact) {
  return {
    reps: [],
    headquarters: buildHeadquarters(siteContact),
  }
}

function normalizeTerritories(territories) {
  if (!Array.isArray(territories)) return []

  return territories
    .filter((territory) => typeof territory === "string")
    .map((territory) => territory.trim().toUpperCase())
    .filter(Boolean)
}

function normalizeLanguages(languages) {
  if (!Array.isArray(languages)) return []

  return languages
    .filter((language) => typeof language === "string")
    .map((language) => language.trim())
    .filter(Boolean)
}

/**
 * State/province of the rep. Uses a CRM state/region field when present,
 * otherwise the name of the rep's own subdivision territory ("Quebec, Canada" -> "Quebec").
 */
export function repRegion(raw) {
  for (const key of ["state", "region", "province"]) {
    if (isFilled(raw?.[key])) return String(raw[key]).trim()
  }

  const territories = Array.isArray(raw?.territories) ? raw.territories : []
  const names = Array.isArray(raw?.territoryNames) ? raw.territoryNames : []
  const country = isFilled(raw?.country) ? String(raw.country).trim() : ""
  if (!country) return ""

  for (let i = 0; i < territories.length; i++) {
    const name = typeof names[i] === "string" ? names[i].trim() : ""
    if (!String(territories[i] || "").includes("-")) continue
    if (!name.endsWith(`, ${country}`)) continue
    return name.slice(0, -(country.length + 2)).trim()
  }

  return ""
}

/** "City, State/province", skipping empty parts. */
export function repPlace(rep) {
  return [rep?.city, rep?.region]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ")
}

export function normalizeRep(raw) {
  if (!raw || typeof raw !== "object") return null

  const name = isFilled(raw.name) ? String(raw.name).trim() : ""
  const email = isFilled(raw.email) ? String(raw.email).trim() : ""
  const phone = isFilled(raw.phone) ? String(raw.phone).trim() : ""
  if (!name && !email && !phone) return null

  return {
    name,
    title: isFilled(raw.title) ? String(raw.title).trim() : "",
    company: isFilled(raw.company) ? String(raw.company).trim() : "",
    email,
    phone,
    city: isFilled(raw.city) ? String(raw.city).trim() : "",
    region: repRegion(raw),
    country: isFilled(raw.country) ? String(raw.country).trim() : "",
    languages: normalizeLanguages(raw.languages),
    territories: normalizeTerritories(raw.territories),
    id: isFilled(raw.id) ? String(raw.id).trim() : "",
    token: readRepToken(raw),
    isHeadquarters: false,
  }
}

export function normalizeRepDirectory(payload, siteContact) {
  const reps = Array.isArray(payload?.reps)
    ? payload.reps.map(normalizeRep).filter(Boolean)
    : []

  return {
    reps,
    headquarters: buildHeadquarters({
      ...siteContact,
      company: payload?.company,
    }),
  }
}

/**
 * Exact subdivision code (CA-ON), then country code (CA), then headquarters.
 */
export function findRepresentative(directory, { countryCode, subdivisionCode } = {}) {
  const reps = directory?.reps || []
  const subdivision = subdivisionCode ? String(subdivisionCode).trim().toUpperCase() : ""
  const country = countryCode ? String(countryCode).trim().toUpperCase() : ""

  if (subdivision) {
    const match = reps.find((rep) => rep.territories.includes(subdivision))
    if (match) return { rep: match, level: "subdivision" }
  }

  if (country) {
    const match = reps.find((rep) => rep.territories.includes(country))
    if (match) return { rep: match, level: "country" }
  }

  return {
    rep: directory?.headquarters || buildHeadquarters(),
    level: "headquarters",
  }
}

/**
 * When false, the picker skips the state/province step and lists every rep in the country.
 * Set to true to bring back the state/province filter.
 */
export const FILTER_BY_REGION = false

/**
 * Reps to show for a selection. With the region filter on, this is the single best match.
 * With it off, it is every rep covering the country or any of its subdivisions.
 * Falls back to headquarters when the country has no rep.
 */
export function findRepresentatives(
  directory,
  { countryCode, subdivisionCode } = {},
  { filterByRegion = FILTER_BY_REGION } = {}
) {
  if (filterByRegion) {
    const { rep, level } = findRepresentative(directory, { countryCode, subdivisionCode })
    return { reps: [rep], level }
  }

  const country = countryCode ? String(countryCode).trim().toUpperCase() : ""
  const reps = country
    ? (directory?.reps || []).filter((rep) =>
        rep.territories.some(
          (territory) => territory === country || territory.startsWith(`${country}-`)
        )
      )
    : []

  if (reps.length) return { reps, level: "country" }

  return {
    reps: [directory?.headquarters || buildHeadquarters()],
    level: "headquarters",
  }
}

export function formatLocation(rep, { level, subdivisionName } = {}) {
  if (!rep) return ""

  const join = (city, region) =>
    [city, region]
      .map((part) => (part || "").trim())
      .filter(Boolean)
      .join(", ")

  if (level === "headquarters" || rep.isHeadquarters) {
    return join(rep.city, rep.region || rep.country)
  }

  if (level === "subdivision" && subdivisionName) {
    return join(rep.city, subdivisionName)
  }

  return join(rep.city, rep.country)
}

export function initials(name) {
  const words = String(name || "")
    .replace(/([a-zà-ÿ])([A-Z])/g, "$1 $2")
    .split(/[\s,]+/)
    .map((word) => word.replace(/^[^A-Za-zÀ-ÖØ-öø-ÿ]+|[^A-Za-zÀ-ÖØ-öø-ÿ]+$/g, ""))
    .filter(Boolean)
    .filter((word) => {
      const letters = word.normalize("NFD").replace(/[^A-Za-z]/g, "")
      return letters && !LEGAL_SUFFIXES.has(letters.toLowerCase())
    })

  const lettersOf = (word) => word.normalize("NFD").replace(/[^A-Za-z]/g, "")

  if (!words.length) return ""
  if (words.length === 1) return lettersOf(words[0]).slice(0, 2).toUpperCase()

  const first = lettersOf(words[0])
  const last = lettersOf(words[words.length - 1])
  return `${first[0] || ""}${last[0] || ""}`.toUpperCase()
}

/** Country code, or the state/province code when one is selected. */
export function placeCode({ countryCode, subdivisionCode } = {}) {
  const subdivision = String(subdivisionCode || "").trim().toUpperCase()
  if (subdivision) return subdivision
  return String(countryCode || "").trim().toUpperCase()
}

export function inquirySubject(code) {
  const place = String(code || "").trim().toUpperCase()
  if (!place) return "Enquiry from solarlab.global"
  return `Enquiry from solarlab.global [W:${place}]`
}

const languageNames = new Intl.DisplayNames(["en"], { type: "language" })

export function formatLanguages(languages) {
  if (!Array.isArray(languages)) return ""

  return languages
    .map((code) => {
      const value = String(code || "").trim()
      if (!value) return ""
      try {
        return languageNames.of(value) || value
      } catch {
        return value
      }
    })
    .filter(Boolean)
    .join(", ")
}

export function telHref(phone) {
  const raw = String(phone || "").trim()
  if (!raw) return ""
  const digits = raw.replace(/\D/g, "")
  if (!digits) return ""
  return `tel:${raw.startsWith("+") ? "+" : ""}${digits}`
}

export function mailtoHref(rep, code) {
  const email = String(rep?.email || "")
    .trim()
    .replace(/[\r\n]/g, "")
  if (!email || /[\s<>?]/.test(email)) return ""
  const subject = encodeURIComponent(inquirySubject(code))
  const cc = encodeURIComponent(INQUIRY_CC)
  return `mailto:${email}?cc=${cc}&subject=${subject}`
}
